/**
 * useDashboardData Hook
 * Centralized API calls for dashboard data
 *
 * Responsibilities:
 * - Call all 4 dashboard data APIs
 * - Return data + loading states
 * - Provide refetch function
 * - NO state management
 * - NO filter logic
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCombinedInvoiceSearch, useInvoiceMetaData, useCombinedInvoiceStats } from 'src/use-cases/invoice/useInvoices';
import type { FiltersDto, InvoiceSearchRequestDto } from 'src/generated/services/TFinancialApi';

interface UseDashboardDataParams {
  filters: {
    currencyReference: string;
    dateReference: InvoiceSearchRequestDto['dateReference'];
    invoiceStatusCategory: InvoiceSearchRequestDto['invoiceStatusCategory'];
    invoiceStatusSet: InvoiceSearchRequestDto['invoiceStatusSet'];
    filters: FiltersDto;
  };
  page: number;
  pageSize: number;
  orderBy: string;
  orderMethod: 'asc' | 'desc';
  keyword?: string | null;
  filterHistory?: Record<string, FiltersDto>;
}

export const useDashboardData = (params: UseDashboardDataParams) => {
  const { filters, page, pageSize, orderBy, orderMethod, keyword, filterHistory = {} } = params;

  // Prepare params for API calls
  const tableParams = {
    page,
    pageSize,
    orderBy,
    orderMethod,
    filters,
    keyword: keyword ?? null,
  };

  /**
   * API Call 1: Invoice Stats (metrics data)
   */
  const { data: dataStats, isLoadingInvoiceStats, isLoadingChargeStats, refetch: refetchStats } = useCombinedInvoiceStats(keyword, filters as any);

  /**
   * API Call 2: Invoice Metadata (Merged Status Counts)
   * 1. Determine the "Active" filters for ACTION_NEEDED (Use current if active, else history/base)
   * 2. Determine the "Active" filters for ALL (Use current if active, else history/base)
   * 3. Call API for both contexts
   * 4. Merge the results
   */
  // Helper to get filters for a specific status category
  const getFiltersForStatus = (targetStatus: InvoiceSearchRequestDto['invoiceStatusCategory']) => {
    // If this is the currently active tab, use the current active filters
    if (filters.invoiceStatusCategory === targetStatus) {
      return filters;
    }

    // Otherwise, try to get from history
    // Always use current context's provider/modality as the base
    const historyFilters = filterHistory[targetStatus];
    const currentProvider = filters.filters?.shipmentFilters?.serviceProviderCode;
    const currentModality = filters.filters?.shipmentFilters?.modality;

    const baseFilters = {
      ...filters,
      invoiceStatusCategory: targetStatus,
      filters: historyFilters
        ? {
            ...historyFilters,
            shipmentFilters: {
              ...historyFilters.shipmentFilters,
              serviceProviderCode: currentProvider,
              modality: currentModality,
            },
          }
        : {
            dateFilters: {},
            tradeLaneFilters: {},
            shipmentFilters: {
              serviceProviderCode: currentProvider,
              modality: currentModality,
            },
          },
    };

    return baseFilters;
  };

  const actionNeededFilters = getFiltersForStatus('ACTION_NEEDED');
  const allExceptionsFilters = getFiltersForStatus('ALL');

  // Call 2a: Metadata for ACTION_NEEDED context
  const { data: actionNeededMeta, isLoading: isLoadingActionMeta, refetch: refetchActionMeta } = useInvoiceMetaData(keyword, actionNeededFilters as any);

  // Call 2b: Metadata for ALL context
  const { data: allExceptionsMeta, isLoading: isLoadingAllMeta, refetch: refetchAllMeta } = useInvoiceMetaData(keyword, allExceptionsFilters as any);

  // Merge the stats
  // We use the count for 'actionNeeded' from the ACTION_NEEDED context
  // And the count for 'all' from the ALL context
  const mergedStatusStats = {
    ...actionNeededMeta?.stats, // Start with Action Needed stats
    ...allExceptionsMeta?.stats, // Overlay All stats (though we really just want specific keys)
    actionNeeded: actionNeededMeta?.stats?.actionNeeded || 0,
    all: allExceptionsMeta?.stats?.all || 0,
  };

  // Use the metadata from the CURRENT tab for allowedCurrencies and other common data
  const currentTabMeta = filters.invoiceStatusCategory === 'ACTION_NEEDED' ? actionNeededMeta : allExceptionsMeta;
  const isLoadingMetadata = isLoadingActionMeta || isLoadingAllMeta;

  /**
   * API Call 3: Invoice List (table data)
   */
  const {
    data: { total: totalItems, items } = {},
    isLoadingInvoiceData,
    isLoadingChargeData,
    refetch: refetchInvoices,
  } = useCombinedInvoiceSearch(tableParams);

  // Query client for manual invalidation
  const queryClient = useQueryClient();

  /**
   * Refetch all dashboard data
   */
  const refetch = useCallback(() => {
    refetchStats();
    refetchActionMeta();
    refetchAllMeta();
    refetchInvoices();
    // Invalidate per-column metadata queries (lazy loaded)
    queryClient.invalidateQueries(['invoiceSearchMetadata']);
  }, [refetchStats, refetchActionMeta, refetchAllMeta, refetchInvoices, queryClient]);

  return {
    // Data
    items: items || [],
    totalItems: totalItems || 0,
    statusStats: mergedStatusStats,
    dataStats: dataStats || {},

    allowedCurrencies: currentTabMeta?.allowedCurrencies || [],

    // Loading states - Granular for progressive loading
    isLoadingInvoiceData,
    isLoadingChargeData,
    isLoadingInvoiceStats,
    isLoadingChargeStats,
    isLoadingMetadata,

    // Actions
    refetch,
  };
};
